import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Box, Slide } from '@mui/material'
import { Snackbar } from '@vale/agata-components-react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import PlataformaService from 'services/PlataformaService'
import * as LayoutManager from './LayoutManager'
import {
  selectPlataformaSelecionada,
  setMenusPersonalizados,
  setMenuPlataformas,
  setPlataformaSelecionada,
  unsetMenusPersonalizados,
  unsetPlataformaSelecionada
} from 'store/slices/plataformaSlice'
import {
  selectSnackbarAtiva,
  unsetSnackbarAtiva
} from 'store/slices/snackbarSlice'
import { Plataforma, Snackbar as SnackbarType } from 'store/slices/types'
import { obterCodPlataformaFormatado } from 'utils/format'
import Utils from 'utils/Utils'
import { Header } from '../../components/Header'
import { useTranslation } from 'react-i18next'
import UsuarioService from 'services/UsuarioService'
import { MenuAjuda } from 'components/MenuAjuda'

export function Layout() {
  const dispatch = useDispatch()
  const { codPlataforma } = useParams()
  const snackbar: SnackbarType = useSelector(selectSnackbarAtiva)
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()
  const plataforma: Plataforma = useSelector(selectPlataformaSelecionada)
  const [carregandoPlataforma, setCarregandoPlataforma] =
    useState<boolean>(false)
  const [carregandoPreferencias, setCarregandoPreferencias] =
    useState<boolean>(true)

  const { i18n } = useTranslation()
  const { accounts } = useMsal()
  const isMicrofrontendPage = location.pathname.includes('/app/')

  useEffect(() => {
    const alteraPlataformaSelecionada = async (codigo: string | undefined) => {
      const menusPlataformas = await LayoutManager.getPlataformasMenu()
      dispatch(setMenuPlataformas(menusPlataformas))

      if (codigo) {
        if (plataforma.codigo != codigo) {
          setCarregandoPlataforma(true)

          dispatch(
            setPlataformaSelecionada({
              ...plataforma,
              codigo: codigo
            })
          )

          const plataformaRecuperada = await PlataformaService.getPlataforma(
            codigo
          )

          if (plataformaRecuperada) {
            if (
              (!Utils.isLocalhost() &&
                location.host == process.env.REACT_APP_DEFAULT_DNS &&
                plataformaRecuperada.nomeHost) ||
              (plataformaRecuperada.nomeHost &&
                location.host != plataformaRecuperada.nomeHost)
            ) {
              location.href = `https://${plataformaRecuperada.nomeHost}${location.pathname}`
            } else {
              dispatch(
                setPlataformaSelecionada({
                  ...plataformaRecuperada,
                  nome:
                    plataformaRecuperada.nome ||
                    obterCodPlataformaFormatado(plataformaRecuperada.codigo)
                })
              )
            }
          }
        }
        const menusPersonalizados = await LayoutManager.getMenusPersonalizados(
          codigo
        )

        dispatch(setMenusPersonalizados(menusPersonalizados))
      } else {
        if (
          !Utils.isLocalhost() &&
          location.host != process.env.REACT_APP_DEFAULT_DNS &&
          (!location.pathname || location.pathname == '/')
        ) {
          setCarregandoPlataforma(true)

          const plataformaAtual =
            await PlataformaService.getPlataformaPorNomeHost(location.host)
          const menusPersonalizados =
            await LayoutManager.getMenusPersonalizados(location.host)

          dispatch(
            setPlataformaSelecionada({
              ...plataformaAtual,
              nome:
                plataformaAtual.nome ||
                obterCodPlataformaFormatado(plataforma.codigo)
            })
          )
          dispatch(setMenusPersonalizados(menusPersonalizados))

          navigate(`/plataformas/${plataforma.codigo}`)
        } else {
          dispatch(unsetPlataformaSelecionada())
          dispatch(unsetMenusPersonalizados())
        }
      }
    }

    if (isAuthenticated) {
      alteraPlataformaSelecionada(codPlataforma)
        .catch(error => {
          dispatch(unsetPlataformaSelecionada())
          dispatch(unsetMenusPersonalizados())
        })
        .finally(() => {
          setCarregandoPlataforma(false)
        })
    }
  }, [codPlataforma, dispatch, isAuthenticated, navigate, plataforma])

  const desativarSnackbar = () => {
    dispatch(unsetSnackbarAtiva())
  }

  const obterUsuarioPreferencias = useCallback(async () => {
    try {
      setCarregandoPreferencias(true)
      const { idioma } = await UsuarioService.getUsuarioPreferencias()
      i18n.changeLanguage(idioma.toLowerCase())
    } catch {
      i18n.changeLanguage('pt')
    } finally {
      setCarregandoPreferencias(false)
    }
  }, [i18n])

  useEffect(() => {
    obterUsuarioPreferencias()
  }, [accounts, obterUsuarioPreferencias])

  return (
    <Box
      id="app-container"
      sx={{
        width: '100%',
        position: 'absolute',
        height: '100%',
        backgroundColor: theme => theme.palette.background.primary,
        overflow: 'auto',
        overflowX: 'hidden',
        ...(isMicrofrontendPage && {
          overflow: undefined,
          overflowX: undefined,
          display: 'flex',
          flexDirection: 'column'
        })
      }}
    >
      {isAuthenticated && !carregandoPreferencias && (
        <>
          <Header carregandoPlataforma={carregandoPlataforma} />
          <Outlet />
        </>
      )}
      <MenuAjuda />
      <Slide direction="left" in={snackbar.open} mountOnEnter unmountOnExit>
        <Snackbar
          open={true}
          severity={snackbar.severity}
          message={snackbar.message}
          horizontal="right"
          vertical="bottom"
          actionMode="close"
          onClose={desativarSnackbar}
          time={5000}
        />
      </Slide>
    </Box>
  )
}
